// CTSK-525 — per-type inline preview body for a document card. Mounted only
// while the card is expanded, so nothing is fetched for collapsed cards.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { DocumentListItem } from '@captain-tasko/shared';
import { NoteMarkdown } from '../notes/NoteMarkdown.js';
import { MimeIcon } from '../MimeIcon.js';
import { formatBytes } from '../../lib/format.js';
import { previewKindFor, type PreviewKind } from './previewKind.js';
import { renderPdfFirstPage } from './pdfThumb.js';

export const PREVIEW_LINES = 12;
/** Scaled mode lays the document out at this "page" width, then shrinks it to the card. */
export const SCALED_PAGE_WIDTH = 816;
export const SCALED_HEIGHT = 260;
const SCALED_LINES = 80;

export function contentUrl(id: string): string {
  return `/api/v1/documents/${id}/content`;
}

/** Renders children at SCALED_PAGE_WIDTH and scales them down to fit the card width.
 *  Clipped at rest; scrolls (subtle bar) while the card is hovered. */
function ScaledFrame({ children }: { children: (scale: number) => ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.4);
  const [contentH, setContentH] = useState(SCALED_HEIGHT);
  useEffect(() => {
    const el = ref.current;
    const inner = innerRef.current;
    if (!el || !inner) return;
    const upd = () => {
      if (el.clientWidth > 0) setScale(Math.min(1, el.clientWidth / SCALED_PAGE_WIDTH));
      setContentH(inner.offsetHeight);
    };
    upd();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    ro.observe(inner);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="doccard-scaled doccard-scroll" style={{ height: SCALED_HEIGHT }} data-testid="doccard-scaled">
      <div style={{ height: Math.max(SCALED_HEIGHT, contentH * scale), position: 'relative' }}>
        <div ref={innerRef} style={{ width: SCALED_PAGE_WIDTH, transform: `scale(${scale})`, transformOrigin: 'top left', position: 'absolute', top: 0, left: 0 }}>
          {children(scale)}
        </div>
      </div>
    </div>
  );
}

/** Same-origin sandboxed iframe sized to its own document so the wrapper (not the frame) scrolls. */
function AutoHeightFrame({ doc, width, minHeight }: { doc: DocumentListItem; width?: number; minHeight: number }) {
  const [h, setH] = useState(minHeight);
  return (
    <iframe
      className="doccard-html"
      title={`Preview of ${doc.title}`}
      src={contentUrl(doc.id)}
      sandbox="allow-same-origin"
      loading="lazy"
      scrolling="no"
      style={{ width: width ?? '100%', height: h, border: 0 }}
      onLoad={(e) => {
        try {
          const d = e.currentTarget.contentDocument;
          const sh = d?.documentElement?.scrollHeight;
          if (sh && sh > 0) setH(Math.max(minHeight, sh));
        } catch { /* cross-origin: keep minHeight */ }
      }}
    />
  );
}

function useTextContent(id: string, enabled: boolean) {
  const [state, setState] = useState<{ text: string | null; error: string | null }>({ text: null, error: null });
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    fetch(contentUrl(id), { credentials: 'include' })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((text) => { if (alive) setState({ text, error: null }); })
      .catch((e: Error) => { if (alive) setState({ text: null, error: e.message }); });
    return () => { alive = false; };
  }, [id, enabled]);
  return state;
}

function firstLines(text: string, n: number): { head: string; more: boolean } {
  const lines = text.split('\n');
  return { head: lines.slice(0, n).join('\n'), more: lines.length > n };
}

function TextBody({ doc, kind, scaled }: { doc: DocumentListItem; kind: 'markdown' | 'text'; scaled: boolean }) {
  const { text, error } = useTextContent(doc.id, true);
  const [full, setFull] = useState(false);
  if (error) return <p className="doccard-note" role="alert">Preview unavailable ({error}).</p>;
  if (text === null) return <p className="doccard-note">Loading…</p>;
  if (scaled) {
    const body = firstLines(text, SCALED_LINES).head;
    return (
      <ScaledFrame>
        {() => (kind === 'markdown' ? <NoteMarkdown source={body} /> : <pre className="doccard-pre" style={{ maxHeight: 'none' }}>{body}</pre>)}
      </ScaledFrame>
    );
  }
  const { head, more } = firstLines(text, PREVIEW_LINES);
  const shown = full ? text : head;
  return (
    <div>
      <div className={`doccard-hero doccard-scroll${full ? ' doccard-hero--full' : ''}`}>
        {kind === 'markdown' ? <NoteMarkdown source={shown} /> : <pre className="doccard-pre">{shown}</pre>}
      </div>
      {more && !full && (
        <button type="button" className="doccard-more" onClick={() => setFull(true)}>Read more</button>
      )}
    </div>
  );
}

function PdfBody({ doc }: { doc: DocumentListItem }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  useEffect(() => {
    let alive = true;
    const canvas = ref.current;
    if (!canvas) return;
    renderPdfFirstPage(contentUrl(doc.id), canvas, 360)
      .then(() => { if (alive) setState('ready'); })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
  }, [doc.id]);
  return (
    <div>
      {state === 'loading' && <p className="doccard-note">Rendering first page…</p>}
      {state === 'error' && <p className="doccard-note" role="alert">PDF preview unavailable.</p>}
      <canvas ref={ref} className="doccard-pdf" aria-label={`First page of ${doc.title}`} hidden={state !== 'ready'} />
    </div>
  );
}

function FallbackBody({ doc }: { doc: DocumentListItem }) {
  return (
    <div className="doccard-fallback">
      <MimeIcon mime={doc.mimeType} size={32} />
      <dl>
        <dt>Type</dt><dd>{doc.mimeType}</dd>
        <dt>Size</dt><dd>{formatBytes(doc.sizeBytes)}</dd>
        <dt>Created</dt><dd>{new Date(doc.createdAt).toLocaleDateString()}</dd>
      </dl>
    </div>
  );
}

export function DocumentPreview({ doc, kind, scaled = false }: { doc: DocumentListItem; kind?: PreviewKind; scaled?: boolean }) {
  const k = kind ?? previewKindFor(doc.mimeType, doc.title);
  switch (k) {
    case 'image':
      return <img className="doccard-img" src={contentUrl(doc.id)} alt={doc.title} loading="lazy" />;
    case 'markdown':
      return <TextBody doc={doc} kind="markdown" scaled={scaled} />;
    case 'text':
      return <TextBody doc={doc} kind="text" scaled={scaled} />;
    case 'pdf':
      return <PdfBody doc={doc} />;
    case 'html':
      if (scaled) {
        return (
          <ScaledFrame>{() => <AutoHeightFrame doc={doc} width={SCALED_PAGE_WIDTH} minHeight={SCALED_HEIGHT} />}</ScaledFrame>
        );
      }
      return (
        <div className="doccard-iframe-wrap doccard-scroll">
          <AutoHeightFrame doc={doc} minHeight={240} />
        </div>
      );
    default:
      return <FallbackBody doc={doc} />;
  }
}
